import { useCallback, useEffect, useState } from 'react'
import type {
  ActivityTotals,
  DailyActivityDoc,
  OrderKind,
  SiteActivityStats
} from '@smorgrav/hundehall_shared'
import { apiFetch } from '@/api'

interface DeployInfo {
  release: string
  environment: string
  targets: Record<string, string>
  deployedAt: string
}

interface LatestOrder {
  siteId: string
  orderId: string
  bookedAt: string
  provider: string
  amount: number
  kind: OrderKind
}

interface ActivitySince {
  since: string
  deploys: DeployInfo[]
  totals: ActivityTotals
  sites: Record<string, SiteActivityStats>
  siteNames: Record<string, string>
  latest: LatestOrder[]
  truncated: boolean
}

interface ActivityDaily {
  days: DailyActivityDoc[]
  today: DailyActivityDoc
  siteNames: Record<string, string>
}

const getJson = async <T>(path: string): Promise<T> => {
  const res = await apiFetch(path)
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status}: ${body}`)
  }
  return (await res.json()) as T
}

// Shared fetch-state plumbing for the two activity endpoints
const useFetched = <T>(path: string) => {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getJson<T>(path))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => {
    void refetch()
  }, [refetch])

  return { data, error, loading, refetch }
}

// `since` empty means "since the latest recorded deploy"
const useActivitySince = (since: string) =>
  useFetched<ActivitySince>(
    `/admin/activity/since${since ? `?since=${encodeURIComponent(since)}` : ''}`
  )

const useActivityDaily = (days: number) =>
  useFetched<ActivityDaily>(`/admin/activity/daily?days=${days}`)

export { useActivityDaily, useActivitySince }
export type { ActivityDaily, ActivitySince, DeployInfo, LatestOrder }
